import { generatePath, matchPath } from 'react-router-dom';
import * as H from 'history';
import { SegmentDefinition } from '@fullstory/proto/fs/api/segment';
import { AppDispatch, AppState, saveUnnamedSegment } from '@fullstory/redux';
import {
  SEGMENT_SEARCH_ROUTE,
  SEGMENT_SEARCH_PAGED_ROUTE,
  ParamsFrom,
  SEGMENT_SEARCH_DETAIL_PAGED_ROUTE,
  SEGMENT_SEARCH_DETAIL_ROUTE,
} from '@fullstory/routes';
import { Flag } from '@fullstory/proto/fs/shared/flags';
import { UnnamedSegment } from '@fullstory/proto/fs/services/dx-objects';

// TODO(craigmccown): Use @fullstory/routes instead
const ROUTE_PATH_PATTERN = '/ui/:orgId/segments/:segmentId/:viewMode(people|searchie):search?';
const USERS_PAGE_ROUTE_PATH_PATTERN = `${ROUTE_PATH_PATTERN}/:page`;

const SEARCHIE_DETAIL_ROUTE_PATH_PATTERN = [
  `${ROUTE_PATH_PATTERN}/:searchieId`,
  `${ROUTE_PATH_PATTERN}/:searchieId::settings`,
];
const SEARCHIE_DETAIL_USERS_PAGE_ROUTE_PATH_PATTERN = [
  `${ROUTE_PATH_PATTERN}/:searchieId/:page`,
  `${ROUTE_PATH_PATTERN}/:searchieId::settings/:page`,
];

type SearchRouteParams = ParamsFrom<typeof SEGMENT_SEARCH_ROUTE>;
type SearchUserPageRouteParams = ParamsFrom<typeof SEGMENT_SEARCH_PAGED_ROUTE>;

type SearchDetailUserPageRouteParams = ParamsFrom<typeof SEGMENT_SEARCH_DETAIL_PAGED_ROUTE>;
type SearchDetailRouteParams = ParamsFrom<typeof SEGMENT_SEARCH_DETAIL_ROUTE>;

export function getNavigateToSegmentDefinitionFn(
  history: H.History,
  dispatch: AppDispatch,
  getState: () => AppState,
  params: SearchRouteParams,
  replace?: boolean,
): (
  segmentDefinition: SegmentDefinition,
  disabled: UnnamedSegment.Disabled,
  name?: string,
  description?: string,
) => void {
  return (
    segmentDefinition: SegmentDefinition,
    disabled: UnnamedSegment.Disabled,
    name?: string,
    description?: string,
  ) => {
    // Produce new unnamed segment path with id backed by persistent storage.
    const unnamedSegment = saveUnnamedSegment(
      dispatch,
      getState,
      segmentDefinition,
      disabled,
      name ?? '',
      description ?? '',
    );
    // Pass along path params and navigate state (to avoid relying on network requests navigating).
    const usidExpr = `:search:${unnamedSegment.id}`;
    const additionalPathParams = { search: usidExpr };
    return getNavigateToSegmentFn(history, params)(additionalPathParams, replace);
  };
}

export function getNavigateToSegmentFn(
  history: H.History,
  params: SearchRouteParams,
): (additionalPathParams: { [key: string]: string | undefined }, replace?: boolean) => void {
  return (additionalPathParams: { [key: string]: string | undefined }, replace?: boolean) => {
    let basePath: string;
    let pathParams: { [key: string]: string };
    if (params.viewMode === 'searchie') {
      // Stay in searchie detail view if already in that view, which requires the searchieId
      // And preserve page if applicable.
      const searchieIdPageMatch = matchPath<
        SearchDetailUserPageRouteParams & { settings?: string }
      >(location.pathname, { path: SEARCHIE_DETAIL_USERS_PAGE_ROUTE_PATH_PATTERN });
      if (searchieIdPageMatch) {
        basePath = searchieIdPageMatch.path;
        pathParams = {
          ...searchieIdPageMatch.params,
          ...additionalPathParams,
        };
      } else {
        const searchieIdMatch = matchPath<SearchDetailRouteParams & { settings?: string }>(
          location.pathname,
          { path: SEARCHIE_DETAIL_ROUTE_PATH_PATTERN },
        );

        if (!searchieIdMatch) {
          basePath = SEGMENT_SEARCH_DETAIL_PAGED_ROUTE.path;
          pathParams = {
            searchieId: Flag[Flag.SearchieMatchingUsers]!,
            page: '0',
            ...additionalPathParams,
          };
        } else {
          basePath = searchieIdMatch.path;
          pathParams = {
            ...searchieIdMatch.params,
            ...additionalPathParams,
          };
        }
      }
    } else {
      const pageMatch = matchPath<SearchUserPageRouteParams>(location.pathname, {
        path: USERS_PAGE_ROUTE_PATH_PATTERN,
      });
      if (pageMatch) {
        basePath = USERS_PAGE_ROUTE_PATH_PATTERN;
        pathParams = {
          ...pageMatch.params,
          ...params,
          ...additionalPathParams,
        };
      } else {
        basePath = ROUTE_PATH_PATTERN;
        pathParams = {
          ...params,
          ...additionalPathParams,
        };
      }
    }
    if (pathParams['page'] && !replace) {
      // Navigating to a new segment / search.
      // Force reset the page.
      pathParams['page'] = '0';
    }
    const path = H.createPath({
      pathname: decodeURIComponent(generatePath(basePath, pathParams)),
      search: history.location.search,
    });
    replace ? history.replace(path) : history.push(path);
  };
}
